/**
 * ErrorBoundary — catches render errors from the lazy-route subtree so a
 * failure shows a friendly reload screen instead of a blank page or an
 * endless LoadingScreen spinner.
 *
 * The most common cause here is a ChunkLoadError: after a new deploy, a
 * visitor with the old index.html cached asks for a route chunk whose
 * content-hashed filename no longer exists. A one-time hard reload fetches the
 * fresh index.html + new chunk names. The reload is guarded by a sessionStorage
 * flag (shared with App's lazyRetry) so it can never loop.
 */
import React from 'react';

const RELOAD_KEY = 'mbegu_chunk_reloaded';

const isChunkError = (e: any) =>
  /ChunkLoadError|Loading chunk|dynamically imported module/i.test(
    `${e?.name ?? ''} ${e?.message ?? ''}`,
  );

interface State { hasError: boolean }

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error) {
    if (isChunkError(error)) {
      try {
        if (sessionStorage.getItem(RELOAD_KEY) !== '1') {
          sessionStorage.setItem(RELOAD_KEY, '1');
          window.location.reload();
        }
      } catch { /* storage blocked → show the fallback UI below */ }
    }
  }

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ minHeight: '60vh', display: 'grid', placeItems: 'center', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 380 }}>
          <p style={{ fontFamily: "'IBM Plex Mono', monospace", color: '#5C7367', fontSize: 12, letterSpacing: '0.12em', textTransform: 'uppercase', margin: '0 0 12px' }}>
            Something didn’t load
          </p>
          <h2 style={{ color: '#13492F', fontWeight: 700, fontSize: 22, margin: '0 0 8px' }}>Let’s try that again</h2>
          <p style={{ color: '#5C7367', fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
            A part of the page failed to load — usually a quick reload fixes it. The seedlings are still here.
          </p>
          <button
            onClick={() => { try { sessionStorage.removeItem(RELOAD_KEY); } catch {} window.location.href = '/'; }}
            style={{ background: '#13492F', color: '#fff', border: 'none', borderRadius: 999, padding: '11px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}
          >
            Reload Mbegu
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
